import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Eye, VolumeX, Trash2, ScrollText, 
  AlertTriangle, ChevronRight, Loader2, Flag,
  CheckCircle, XCircle, User, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { useUserData } from './UserDataContext';
import { WatcherChatInspectorModal } from './WatcherChatInspectorModal';
import {
  muteUser, unmuteUser, softDeleteYap,
  logAuditAction, MuteDuration
} from '../services/WatcherService';
import { AuditFeed } from './AuditFeed';
import { getDbSafe } from '../services/firebaseConfig';
import { doc, getDoc, collection, query, where, getDocs, limit, onSnapshot, orderBy, updateDoc, serverTimestamp } from 'firebase/firestore';
import { getAuthSafe } from '../services/firebaseConfig';

type PanelView = 'home' | 'mute' | 'delete' | 'audit' | 'reports';

interface WatcherActionPanelProps {
  onClose: () => void;
  onNavigateToReports?: () => void;
}

const MUTE_DURATIONS: { label: string; value: MuteDuration }[] = [
  { label: '1 Hour',    value: '1h' },
  { label: '24 Hours',  value: '24h' },
  { label: '7 Days',    value: '7d' },
  { label: '30 Days',   value: '30d' },
  { label: 'Permanent', value: 'permanent' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function RedInput({ placeholder, value, onChange, multiline }: {
  placeholder: string; value: string; onChange: (v: string) => void; multiline?: boolean;
}) {
  const base = "w-full bg-[#111] border border-[#DC2626]/30 focus:border-[#DC2626] text-white placeholder-white/30 rounded-xl px-3 py-2.5 text-sm outline-none transition-colors resize-none";
  return multiline
    ? <textarea className={`${base} min-h-[80px]`} placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)} />
    : <input className={base} placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)} />;
}

function UserAutocompleteDropdown({ value, onChange }: { value: string; onChange: (v: string) => void; }) {
  const [results, setResults] = useState<{id: string, username: string, name: string}[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    if (value.length < 2) {
      setResults([]);
      setShowDropdown(false);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const db = await getDbSafe();
        let search = value.trim().toLowerCase();
        if (!search.startsWith('@')) search = `@${search}`;
        
        const q = query(
          collection(db, 'users'), 
          where('username', '>=', search), 
          where('username', '<=', search + '\uf8ff'), 
          limit(5)
        );
        const snap = await getDocs(q);
        const matches = snap.docs.map(d => ({ id: d.id, username: d.data().username || '', name: d.data().name || 'Anonymous' }));
        setResults(matches);
        setShowDropdown(true);
      } catch (e) {
        console.error(e);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [value]);

  return (
    <div className="relative">
      <RedInput 
         placeholder="Target Username (e.g. @john)" 
         value={value} 
         onChange={(v) => { onChange(v); setShowDropdown(true); }} 
      />
      {showDropdown && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-[#111] border border-[#DC2626]/30 rounded-xl overflow-hidden z-50 shadow-2xl max-h-48 overflow-y-auto">
          {results.map(user => (
            <button
               key={user.id}
               className="w-full text-left px-3 py-2.5 hover:bg-[#DC2626]/20 transition-colors flex flex-col border-b border-white/5 last:border-0"
               onClick={() => {
                 onChange(user.username);
                 setResults([]);
                 setShowDropdown(false);
               }}
            >
              <span className="text-white text-sm font-semibold">{user.name}</span>
              <span className="text-white/50 text-xs">{user.username}</span>
            </button>
          ))}
        </div>
      )}

    </div>
  );
}

function RedButton({ onClick, loading, children, ghost }: {
  onClick: () => void; loading?: boolean; children: React.ReactNode; ghost?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className={`w-full py-2.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all active:scale-95 disabled:opacity-50 ${ghost
        ? 'bg-white/5 hover:bg-white/10 text-white/70 border border-white/10'
        : 'text-white'
      }`}
      style={ghost ? {} : { background: '#DC2626', boxShadow: '0 0 16px rgba(220,38,38,0.4)' }}
    >
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : children}
    </button>
  );
}

// ─── Sub-views ────────────────────────────────────────────────────────────────

function MuteView({ watcherId, watcherUsername, onBack }: { watcherId: string; watcherUsername: string; onBack: () => void }) {
  const [targetUsernameInput, setTargetUsernameInput] = useState('');
  const [reason, setReason]         = useState('');
  const [duration, setDuration]     = useState<MuteDuration>('24h');
  const [loading, setLoading]       = useState(false);

  const resolveUserId = async (username: string) => {
    const db = await getDbSafe();
    let searchName = username.trim();
    if (!searchName.startsWith('@')) searchName = `@${searchName}`;
    const q = query(collection(db, 'users'), where('username', 'in', [searchName, searchName.substring(1)]));
    const snap = await getDocs(q);
    if (snap.empty) return null;
    return snap.docs[0].id;
  };

  const handleMute = async () => {
    if (!targetUsernameInput.trim() || !reason.trim()) { toast.error('Username and reason are required.'); return; }
    setLoading(true);
    try {
      const actualTargetId = await resolveUserId(targetUsernameInput);
      if (!actualTargetId) {
        toast.error('User not found');
        return;
      }
      await muteUser(watcherId, watcherUsername, actualTargetId, duration, reason.trim());
      toast.success(`User muted for ${duration}`);
      setTargetUsernameInput(''); setReason('');
    } catch (e) {
      toast.error('Failed to mute user');
    } finally { setLoading(false); }
  };

  const handleUnmute = async () => {
    if (!targetUsernameInput.trim()) { toast.error('Username is required.'); return; }
    setLoading(true);
    try {
      const actualTargetId = await resolveUserId(targetUsernameInput);
      if (!actualTargetId) {
        toast.error('User not found');
        return;
      }
      await unmuteUser(watcherId, watcherUsername, actualTargetId, reason.trim() || 'Watcher unmute');
      toast.success('User unmuted');
      setTargetUsernameInput(''); setReason('');
    } catch (e) {
      toast.error('Failed to unmute');
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="text-xs text-[#DC2626] flex items-center gap-1 mb-1">← Back</button>
      <p className="text-white/50 text-xs uppercase tracking-widest font-bold">Mute / Unmute User</p>
      <UserAutocompleteDropdown value={targetUsernameInput} onChange={setTargetUsernameInput} />
      <RedInput placeholder="Reason (required)" value={reason} onChange={setReason} multiline />
      <div>
        <p className="text-white/40 text-[11px] mb-1.5 uppercase tracking-wider">Duration</p>
        <div className="grid grid-cols-2 gap-1.5">
          {MUTE_DURATIONS.map(d => (
            <button
              key={d.value}
              onClick={() => setDuration(d.value)}
              className="py-1.5 rounded-lg text-sm font-semibold transition-all"
              style={{
                background: duration === d.value ? '#DC2626' : 'rgba(255,255,255,0.05)',
                color: duration === d.value ? '#fff' : 'rgba(255,255,255,0.5)',
                border: duration === d.value ? '1px solid #DC2626' : '1px solid rgba(255,255,255,0.08)',
              }}
            >
              {d.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex gap-2">
        <RedButton onClick={handleMute} loading={loading}>🔇 Mute</RedButton>
        <RedButton onClick={handleUnmute} loading={loading} ghost>🔊 Unmute</RedButton>
      </div>
    </div>
  );
}

function DeleteView({ watcherId, watcherUsername, onBack }: { watcherId: string; watcherUsername: string; onBack: () => void }) {
  const [yapId, setYapId]     = useState('');
  const [reason, setReason]   = useState('');
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    if (!yapId.trim() || !reason.trim()) { toast.error('Yap ID and reason are required.'); return; }
    setLoading(true);
    try {
      const db = await getDbSafe();

      // Search across all collections where yaps can live
      const YAP_COLS = ['yaps', 'forum_posts', 'tribe_posts'];
      let data: any = null;
      for (const col of YAP_COLS) {
        const snap = await getDoc(doc(db, col, yapId.trim()));
        if (snap.exists()) { data = snap.data(); break; }
      }

      if (!data) { toast.error('Yap not found'); setLoading(false); return; }

      await softDeleteYap(watcherId, watcherUsername, yapId.trim(), data.userId || data.authorId || '', data.content || '', reason.trim());
      toast.success('Yap removed & user notified');
      setYapId(''); setReason('');
    } catch (e) {
      console.error('[WatcherActionPanel] Delete failed:', e);
      toast.error('Failed to delete yap');
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="text-xs text-[#DC2626] flex items-center gap-1 mb-1">← Back</button>
      <p className="text-white/50 text-xs uppercase tracking-widest font-bold">Soft-Delete Yap</p>
      <RedInput placeholder="Yap ID" value={yapId} onChange={setYapId} />
      <RedInput placeholder="Reason for removal (required — sent to user)" value={reason} onChange={setReason} multiline />
      <div className="flex items-start gap-2 bg-[#DC2626]/10 border border-[#DC2626]/25 rounded-xl px-3 py-2">
        <AlertTriangle className="w-4 h-4 text-[#DC2626] shrink-0 mt-0.5" />
        <p className="text-[#DC2626] text-xs">Soft-delete only. The yap is hidden, not erased. This action is permanently logged.</p>
      </div>
      <RedButton onClick={handleDelete} loading={loading}>🗑️ Confirm Removal</RedButton>
    </div>
  );
}

// ─── Reports View ──────────────────────────────────────────────────────────────

/**
 * One description of every report type, replacing four hardcoded maps that had
 * drifted apart: a lookup table keyed on `forum`/`group`, an inspect chain keyed
 * on the same wrong names, a hide-content map keyed on the right ones, and a
 * colour map that was never referenced at all.
 *
 * The wrong keys were invisible because both offenders failed the same quiet
 * way — a lookup that finds nothing renders '[No Text Content]', and an inspect
 * with no branch copies an id to the clipboard. A forum-post report has been
 * unreviewable for exactly as long as the DM one was.
 *
 * Content resolution has two modes on purpose:
 *   lookupCollections — fetch the live document (yaps, shouts, group posts)
 *   contentFromReport — read what the reporter captured, no fetch
 * The second is not a fallback for missing plumbing. For dm, room and
 * room_message there is no single document to fetch, and for rooms the content
 * is deleted within hours by design, so a live read would fail precisely when a
 * moderator needs it. Every type also falls back to the report snapshot when a
 * live lookup finds nothing, which is what happens once content is deleted.
 */
export type ReportTargetType =
  | 'user' | 'yap' | 'reply' | 'shout' | 'comment'
  | 'event' | 'event_reply' | 'event_comment'
  | 'dm' | 'forum_post' | 'tribe_post'
  | 'room' | 'room_message';

interface InspectCtx {
  /** Leaves the panel for another screen; the panel closes behind you. */
  navigate: (eventName: string, detail: any) => void;
  /** Opens a modal ON TOP of the panel, which stays put underneath. */
  openChatInspector: (chatId: string, label: string) => void;
  toast: typeof toast;
}

export interface ReportTypeConfig {
  label: string;
  /** Badge classes. */
  color: string;
  /** Accent colour for the row's left border, so types are distinguishable at a glance. */
  accent: string;
  /** Collections to try, in order. Absent means a live lookup is wrong, not merely unavailable. */
  lookupCollections?: string[];
  /** Preview taken straight from the report document. */
  contentFromReport?: (r: any) => string | null;
  /** false hides the Hide Content button rather than letting it fail. */
  canHideContent: boolean;
  /** Collections Hide Content should try. Defaults to lookupCollections. */
  hideCollections?: string[];
  inspect?: (r: any, ctx: InspectCtx) => void;
}

/** Reporters store the offending text under a few different keys. */
const reportedText = (r: any): string | null => {
  const m = r?.metadata || {};
  const text = m.content || m.caption || m.text || m.snapshot?.text || '';
  if (text) return String(text);
  if (m.snapshot && (m.snapshot.imageUrl || m.snapshot.audioUrl || m.snapshot.videoUrl)) return '[Media Content]';
  return null;
};

export const REPORT_TYPE_CONFIG: Record<ReportTargetType, ReportTypeConfig> = {
  user: {
    label: 'User', color: 'text-purple-400 bg-purple-400/10', accent: '#c084fc',
    canHideContent: false,
    inspect: (r, { navigate }) => navigate('navigateToFriendProfile', { id: r.targetUserId || r.reportedId }),
  },
  yap: {
    label: 'Yap', color: 'text-blue-400 bg-blue-400/10', accent: '#60a5fa',
    lookupCollections: ['yaps'], contentFromReport: reportedText, canHideContent: true,
    inspect: (r, { navigate }) => navigate('navigateToYapDetail', { id: r.reportedId }),
  },
  reply: {
    label: 'Reply', color: 'text-cyan-400 bg-cyan-400/10', accent: '#22d3ee',
    lookupCollections: ['yaps'], contentFromReport: reportedText, canHideContent: true,
    // A reply id is not a yap id, so the parent from metadata is what opens.
    inspect: (r, { navigate }) => navigate('navigateToYapDetail', { id: r.metadata?.yapId || r.metadata?.postId || r.reportedId }),
  },
  comment: {
    label: 'Comment', color: 'text-cyan-400 bg-cyan-400/10', accent: '#22d3ee',
    lookupCollections: ['yaps'], contentFromReport: reportedText, canHideContent: true,
    inspect: (r, { navigate }) => navigate('navigateToYapDetail', { id: r.metadata?.yapId || r.metadata?.postId || r.reportedId }),
  },
  shout: {
    label: 'Shout', color: 'text-orange-400 bg-orange-400/10', accent: '#fb923c',
    lookupCollections: ['shouts'], contentFromReport: reportedText, canHideContent: true,
    inspect: (r, { navigate }) => navigate('navigateToShoutDetail', { id: r.reportedId }),
  },
  forum_post: {
    label: 'Forum Post', color: 'text-amber-400 bg-amber-400/10', accent: '#fbbf24',
    // Cross-posted forum content can live in either collection.
    lookupCollections: ['forum_posts', 'yaps'], contentFromReport: reportedText, canHideContent: true,
    // reportedId is the POST; the forum to open is in metadata. Passing the post
    // id to the forum screen — which the old branch did, under the wrong key —
    // would have opened nothing even once the key was right.
    inspect: (r, { navigate, toast: t }) => {
      const forumId = r.metadata?.forumId;
      if (!forumId) { t.error('No forum reference on this report'); return; }
      navigate('navigateToHistoryForum', { id: forumId });
    },
  },
  tribe_post: {
    label: 'Tribe Post', color: 'text-green-400 bg-green-400/10', accent: '#4ade80',
    lookupCollections: ['tribe_posts'], contentFromReport: reportedText, canHideContent: true,
    inspect: (r, { navigate, toast: t }) => {
      const tribeId = r.metadata?.tribeId;
      if (!tribeId) { t.error('No tribe reference on this report'); return; }
      navigate('navigateToTribeGroup', { id: tribeId });
    },
  },
  event: {
    label: 'Event', color: 'text-pink-400 bg-pink-400/10', accent: '#f472b6',
    lookupCollections: ['events'], contentFromReport: reportedText, canHideContent: false,
    inspect: (r, { navigate }) => navigate('navigateToDetails', { id: r.metadata?.eventId || r.reportedId }),
  },
  event_reply: {
    label: 'Event Reply', color: 'text-pink-400 bg-pink-400/10', accent: '#f472b6',
    contentFromReport: reportedText, canHideContent: false,
    inspect: (r, { navigate, toast: t }) => {
      const eventId = r.metadata?.eventId;
      if (!eventId) { t.error('No event reference on this report'); return; }
      navigate('navigateToDetails', { id: eventId });
    },
  },
  event_comment: {
    label: 'Event Comment', color: 'text-pink-400 bg-pink-400/10', accent: '#f472b6',
    contentFromReport: reportedText, canHideContent: false,
    inspect: (r, { navigate, toast: t }) => {
      const eventId = r.metadata?.eventId;
      if (!eventId) { t.error('No event reference on this report'); return; }
      navigate('navigateToDetails', { id: eventId });
    },
  },
  dm: {
    label: 'DM', color: 'text-red-400 bg-red-400/10', accent: '#f87171',
    // A DM report covers a conversation, not a message: nothing to quote.
    contentFromReport: () => null,
    canHideContent: false,
    // NOT a navigation. DirectMessageScreen resolves its conversation through
    // MessagingContext, which only knows the viewer's own chats — a Watcher is
    // in neither seat, so that screen renders "Start a conversation" no matter
    // what is passed to it. The transcript is fetched by chatId instead.
    inspect: (r, { openChatInspector, toast: t }) => {
      const chatId = r.metadata?.chatId;
      if (!chatId) { t.error('No chat reference on this report'); return; }
      openChatInspector(chatId, r.reportedUsername || r.metadata?.reportedUsername || r.targetUserId || 'this user');
    },
  },
  room: {
    label: 'Room', color: 'text-red-400 bg-red-400/10', accent: '#f87171',
    // The premise of the room, captured at report time — the room itself is
    // usually gone by review time.
    contentFromReport: (r) => {
      const title = r?.metadata?.title;
      const rules = r?.metadata?.rules;
      if (!title && !rules) return null;
      return rules ? `"${title || 'Untitled room'}" — rules: ${rules}` : `"${title}"`;
    },
    canHideContent: false,
    // Live navigation is a bonus when the room happens to still exist; the
    // snapshot above is the reliable review surface.
    inspect: (r, { navigate, toast: t }) => {
      const roomId = r.metadata?.roomId || r.reportedId;
      if (!roomId) { t.error('No room reference on this report'); return; }
      navigate('navigateToRoom', { id: roomId });
    },
  },
  room_message: {
    label: 'Room Message', color: 'text-red-400 bg-red-400/10', accent: '#f87171',
    contentFromReport: reportedText,
    canHideContent: false,
    // Deliberately no live navigation: by review time the message and its room
    // have almost always been purged, so the snapshot is the whole surface.
    inspect: (r, { navigate, toast: t }) => {
      const roomId = r.metadata?.roomId;
      if (!roomId) { t.error('This room has ended; review the snapshot above'); return; }
      navigate('navigateToRoom', { id: roomId });
    },
  },
};

const configFor = (type?: string): ReportTypeConfig | undefined =>
  type ? REPORT_TYPE_CONFIG[type as ReportTargetType] : undefined;

function ReportsView({ watcherId, watcherUsername, onBack, onClose }: {
  watcherId: string;
  watcherUsername: string;
  onBack: () => void;
  onClose: () => void;
}) {
  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Read-only DM transcript, layered over the queue rather than replacing it.
  const [inspectingChat, setInspectingChat] = useState<{ chatId: string; label: string } | null>(null);
  // resolved author usernames for non-user reports keyed by reportedId
  const [authorMap, setAuthorMap] = useState<Record<string, string>>({});

  const [contentMap, setContentMap] = useState<Record<string, string>>({});

  // Resolve author username & content for shout/yap/etc. reports
  const resolveDetails = async (rpts: any[]) => {
    const authorUpdates: Record<string, string> = {};
    const contentUpdates: Record<string, string> = {};

    // Snapshot first, for every type that carries one. It costs no request, and
    // it is the only thing that still exists once the reported content is
    // deleted — which for a room is a matter of hours.
    for (const r of rpts) {
      const fromReport = configFor(r.targetType)?.contentFromReport?.(r);
      if (fromReport && r.reportedId) contentUpdates[r.reportedId] = fromReport;
    }

    // Only types with a live document get fetched. A type with no
    // lookupCollections is not unhandled — it has nothing to look up, and
    // falling back to ['yaps','shouts'] is exactly what made DM and forum_post
    // reports read as though their content had vanished.
    const needsResolution = rpts.filter(r =>
      r.targetType !== 'user'
      && r.reportedId
      && (configFor(r.targetType)?.lookupCollections?.length ?? 0) > 0
    );

    if (!needsResolution.length) {
      if (Object.keys(contentUpdates).length) setContentMap(prev => ({ ...prev, ...contentUpdates }));
      return;
    }

    try {
      const db = await getDbSafe();

      await Promise.all(needsResolution.map(async r => {
        const cols = configFor(r.targetType)?.lookupCollections || [];
        for (const col of cols) {
          try {
            const snap = await getDoc(doc(db, col, r.reportedId));
            if (snap.exists()) {
              const data = snap.data();
              
              // 1. Resolve content
              const content = data.content || data.caption || data.text || data.shoutCaption || data.originalYap?.content || data.originalYap?.text || '';
              if (content) {
                contentUpdates[r.reportedId] = content;
              } else if (data.mediaUrl || data.audioUrl || data.imageUrl || data.shoutImages?.length || data.originalYap?.mediaUrl || data.originalYap?.audioUrl || data.originalYap?.imageUrl) {
                contentUpdates[r.reportedId] = '[Media Content]';
              }
              // A live document that exists but has been emptied should not
              // overwrite a good snapshot with nothing.
              if (!contentUpdates[r.reportedId]) {
                const snap2 = configFor(r.targetType)?.contentFromReport?.(r);
                if (snap2) contentUpdates[r.reportedId] = snap2;
              }
              
              // 2. Resolve username if not already stored on report
              if (!r.reportedUsername) {
                const username = data.username || data.authorUsername || data.author || '';
                if (username) { authorUpdates[r.reportedId] = username; }
                else {
                  // fallback: look up from userId
                  const uid = data.userId || data.authorId || data.shoutedBy;
                  if (uid) {
                    const userSnap = await getDoc(doc(db, 'users', uid));
                    if (userSnap.exists()) {
                      authorUpdates[r.reportedId] = userSnap.data().username || uid;
                    }
                  }
                }
              }
              break; // Found the item, no need to check next collection
            }
          } catch { /* skip */ }
        }
      }));
      if (Object.keys(authorUpdates).length) {
        setAuthorMap(prev => ({ ...prev, ...authorUpdates }));
      }
      if (Object.keys(contentUpdates).length) {
        setContentMap(prev => ({ ...prev, ...contentUpdates }));
      }
    } catch { /* silent */ }
  };

  useEffect(() => {
    let unsub: (() => void) | null = null;
    (async () => {
      try {
        const db = await getDbSafe();
        const q = query(
          collection(db, 'reports'),
          where('status', '==', 'pending'),
          orderBy('timestamp', 'desc'),
          limit(30)
        );
        unsub = onSnapshot(q, snap => {
          const rpts = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          setReports(rpts);
          setLoading(false);
          resolveDetails(rpts);
        }, () => setLoading(false));
      } catch {
        setLoading(false);
      }
    })();
    return () => { unsub?.(); };
  }, []);

  // Permanent resolution log — never deletes the report (keeps it as an audit
  // trail). status: 'resolved' | 'actioned' | 'dismissed'. Records both the
  // legacy resolvedBy and the spec resolvedByMod/resolvedAt.
  const resolveReport = async (reportId: string, status: 'resolved' | 'actioned' | 'dismissed', note: string) => {
    const db = await getDbSafe();
    const auth = await getAuthSafe();
    const modId = auth.currentUser?.uid || watcherId;
    await updateDoc(doc(db, 'reports', reportId), {
      status,
      resolvedBy: modId,
      resolvedByMod: modId,
      resolvedAt: serverTimestamp(),
      resolutionNote: `${note} by ${watcherUsername?.startsWith('@') ? watcherUsername : `@${watcherUsername}`}`,
    });
  };

  const handleAction = async (reportId: string, status: 'resolved' | 'dismissed') => {
    setProcessing(reportId);
    try {
      await resolveReport(reportId, status, status);
      toast.success(status === 'resolved' ? '✅ Report resolved' : '✕ Report dismissed');
      setExpandedId(null);
    } catch {
      toast.error('Failed to update report');
    } finally {
      setProcessing(null);
    }
  };

  // Owner of the reported content/user — prefers the normalized targetUserId,
  // falls back to legacy fields for older report docs.
  const reportTargetUserId = (r: any): string =>
    String(r.targetUserId || r.metadata?.authorId || r.metadata?.shouterId || (r.targetType === 'user' ? r.reportedId : '') || '');

  const handleWarn = async (r: any) => {
    const targetUserId = reportTargetUserId(r);
    if (!targetUserId) { toast.error('No target user on this report'); return; }
    setProcessing(r.id);
    try {
      const { NotificationService } = await import('../services/api');
      await NotificationService.sendNotification(
        targetUserId,
        'system',
        'Moderator Warning',
        `@${(watcherUsername || 'a moderator').replace(/^@/, '')} reviewed a report about your ${r.targetType === 'user' ? 'account' : 'content'} regarding "${r.reason}". This is a warning — please review the community guidelines to avoid further action.`,
        { reportId: r.id, moderation: true, watcherUsername }
      );
      await resolveReport(r.id, 'actioned', 'warned user');
      await logAuditAction({
        watcher_id: watcherId,
        watcher_username: watcherUsername,
        target_user_id: targetUserId,
        action_type: 'warn_user',
        reason: r.reason || 'Reported content',
        timestamp: serverTimestamp(),
        metadata: { reportId: r.id, reportType: r.targetType },
      }).catch(() => {});
      toast.success('⚠️ User warned');
      setExpandedId(null);
    } catch {
      toast.error('Failed to warn user');
    } finally {
      setProcessing(null);
    }
  };

  const handleHideContent = async (r: any) => {
    if (r.targetType === 'user') { toast.error('Use Hide Account for user reports'); return; }
    const cfg = configFor(r.targetType);
    // The button is hidden for these types, so reaching here means a legacy
    // report type. Refuse rather than hunting for a document that cannot exist.
    if (cfg && !cfg.canHideContent) {
      toast.error(`${cfg.label} reports have no content to hide`);
      return;
    }
    const contentId = r.targetId || r.reportedId;
    if (!contentId) { toast.error('No content ID on this report'); return; }
    setProcessing(r.id);
    try {
      const db = await getDbSafe();
      const candidates: string[] = cfg?.hideCollections || cfg?.lookupCollections || ['yaps'];
      let hidden = false;
      for (const coll of candidates) {
        const ref = doc(db, coll, String(contentId));
        const snap = await getDoc(ref);
        if (snap.exists()) {
          await updateDoc(ref, { is_hidden: true, hiddenByMod: watcherId, hiddenAt: serverTimestamp() });
          hidden = true;
          break;
        }
      }
      if (!hidden) { toast.error('Content not found to hide'); return; }
      await resolveReport(r.id, 'actioned', 'hid content');
      await logAuditAction({
        watcher_id: watcherId,
        watcher_username: watcherUsername,
        target_user_id: reportTargetUserId(r),
        action_type: 'hide_content',
        reason: r.reason || 'Reported content',
        timestamp: serverTimestamp(),
        metadata: { reportId: r.id, contentId, contentType: r.targetType, contentSnippet: (contentMap[r.reportedId] || '').slice(0, 200) },
      }).catch(() => {});
      toast.success('🚫 Content hidden');
      setExpandedId(null);
    } catch {
      toast.error('Failed to hide content');
    } finally {
      setProcessing(null);
    }
  };

  const handleHideAccount = async (r: any) => {
    const targetUserId = reportTargetUserId(r);
    if (!targetUserId) { toast.error('No target user on this report'); return; }
    if (!window.confirm("Hide this user's account for a portfolio violation? They'll be notified and DM'd to update their photos.")) return;
    setProcessing(r.id);
    try {
      const { hideAccountForPortfolioViolation } = await import('../services/WatcherService');
      await hideAccountForPortfolioViolation(targetUserId);
      await resolveReport(r.id, 'actioned', 'hid account');
      toast.success('🙈 Account hidden');
      setExpandedId(null);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to hide account');
    } finally {
      setProcessing(null);
    }
  };

  const handleInspect = (r: any) => {
    const cfg = configFor(r.targetType);
    // Closing belongs to the action, not to handleInspect: leaving for another
    // screen should close the panel, opening a modal over it should not — the
    // moderator is still working the queue underneath.
    const navigate = (eventName: string, detail: any) => {
      window.dispatchEvent(new CustomEvent(eventName, { detail }));
      onClose();
    };
    const openChatInspector = (chatId: string, label: string) => {
      setInspectingChat({ chatId, label });
    };

    // The clipboard fallback is for a type this build has never heard of — a
    // legacy or future report — not for known types whose branch nobody wrote.
    if (!cfg?.inspect) {
      const id = r.reportedId;
      if (!id) { toast.error('No content ID on this report'); return; }
      navigator.clipboard.writeText(id).catch(() => {});
      toast.success('Content ID copied: ' + id);
      return;
    }

    cfg.inspect(r, { navigate, openChatInspector, toast });
  };

  const handleForceEndRoom = async (r: any) => {
    const roomId = r.metadata?.roomId || (r.targetType === 'room' ? r.reportedId : null);
    if (!roomId) { toast.error('No room reference on this report'); return; }
    if (!window.confirm('End this room now? Everything in it is deleted.')) return;
    setProcessing(r.id);
    try {
      const { RoomService } = await import('../services/RoomService');
      await RoomService.forceEndRoom(roomId, watcherId, watcherUsername, r.reason || 'Reported content');
      await resolveReport(r.id, 'actioned', 'force-ended room');
      toast.success('Room ended');
      setExpandedId(null);
    } catch (e: any) {
      toast.error(e?.message || 'Could not end that room');
    } finally {
      setProcessing(null);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-1">
        <button onClick={onBack} className="text-xs text-[#DC2626] flex items-center gap-1">← Back</button>
        <p className="text-white/50 text-xs uppercase tracking-widest font-bold">Reports</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 text-[#DC2626] animate-spin" /></div>
      ) : reports.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-8">
          <CheckCircle className="w-8 h-8 text-green-500" />
          <p className="text-white/30 text-sm">No pending reports. All clear!</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {reports.map(r => {
            const isExpanded = expandedId === r.id;
            const isProcessing = processing === r.id;
            // Resolve display name: use stored reportedUsername, then the copy
            // nested in metadata (DM reports send it there, and WatcherService
            // does not flatten metadata onto the report), then authorMap.
            const displayName = r.reportedUsername || r.metadata?.reportedUsername || authorMap[r.reportedId] || '';
            const cfg = configFor(r.targetType);
            return (
              <div
                key={r.id}
                className="rounded-xl border border-white/8 overflow-hidden"
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  // Every row used to look identical, with the type legible only
                  // as a raw uppercased key. The accent makes the queue skimmable.
                  borderLeft: `3px solid ${cfg?.accent || '#6b7280'}`,
                }}
              >
                {/* Summary row */}
                <button
                  className="w-full flex items-start gap-3 px-3 py-2.5 text-left"
                  onClick={() => setExpandedId(isExpanded ? null : r.id)}
                >
                  <AlertTriangle className="w-3.5 h-3.5 text-[#DC2626] shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-bold text-xs truncate">
                      <span
                        className={`px-1.5 py-0.5 rounded ${cfg?.color || 'text-white/70 bg-white/10'}`}
                        style={{ fontSize: 10, fontWeight: 700 }}
                      >
                        {cfg?.label || (r.targetType || 'unknown').toUpperCase()}
                      </span>
                      {displayName && (
                        <span className="text-white/60 font-normal"> from </span>
                      )}
                      {displayName && (
                        <span className="text-white">@{displayName.replace(/^@/, '')}</span>
                      )}
                    </p>
                    <p className="text-white/40 text-[11px] truncate mt-0.5">{r.category} · {r.reason}</p>
                  </div>
                  <ChevronRight
                    className={`w-3.5 h-3.5 text-white/30 shrink-0 mt-1 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                  />
                </button>

                {/* Expanded actions */}
                {isExpanded && (
                  <div className="px-3 pb-3 pt-1 border-t border-white/6 space-y-2">
                    {(r.reportedUsername || r.metadata?.reportedUsername) && (
                      <p className="text-white/50 text-[11px]">
                        Target: <span className="text-white font-semibold">@{String(r.reportedUsername || r.metadata?.reportedUsername).replace(/^@/, '')}</span>
                      </p>
                    )}
                    <p className="text-white/80 text-[12px] italic line-clamp-3">
                      {r.targetType === 'user'
                        ? 'User Profile Report'
                        : contentMap[r.reportedId]
                          ? `"${contentMap[r.reportedId]}"`
                          : r.targetType === 'dm'
                            // Not an empty message: a DM report covers a whole
                            // conversation, so there is nothing to quote.
                            ? 'DM conversation: open Inspect Content to review it'
                            : cfg && !cfg.lookupCollections
                              // Snapshot-only type whose snapshot is missing —
                              // an old report filed before snapshots were stored.
                              ? `${cfg.label} report: no content was captured`
                              : '[No Text Content]'
                      }
                    </p>

                    {/* Inspect content button */}
                    <button
                      onClick={() => handleInspect(r)}
                      className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-bold text-white/80 transition-all active:scale-95 hover:text-white"
                      style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }}
                    >
                      <ExternalLink className="w-3 h-3" />
                      Inspect Content
                    </button>

                    {/* Moderation actions — warn the owner or hide the content */}
                    <div className="flex gap-2">
                      <button
                        disabled={!!isProcessing}
                        onClick={() => handleWarn(r)}
                        className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-bold text-white transition-all active:scale-95 disabled:opacity-50"
                        style={{ background: '#d97706' }}
                      >
                        {isProcessing ? <Loader2 className="w-3 h-3 animate-spin" /> : <AlertTriangle className="w-3 h-3" />}
                        Warn User
                      </button>
                      {/* Only where a persistent document exists. dm, room,
                          room_message and event reports used to show this button
                          and fail with "Content not found to hide". */}
                      {r.targetType !== 'user' && (cfg ? cfg.canHideContent : true) && (
                        <button
                          disabled={!!isProcessing}
                          onClick={() => handleHideContent(r)}
                          className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-bold text-white transition-all active:scale-95 disabled:opacity-50"
                          style={{ background: '#b91c1c' }}
                        >
                          {isProcessing ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
                          Hide Content
                        </button>
                      )}
                    </div>

                    {/* Watcher force-end. Sets status 'closed'; sweepExpiredRooms
                        purges it on its next pass, the same route an owner-ended
                        room takes. Silent by design, matching the existing
                        decision not to notify on moderation actions. */}
                    {(r.targetType === 'room' || r.targetType === 'room_message') && (
                      <button
                        disabled={!!isProcessing}
                        onClick={() => handleForceEndRoom(r)}
                        className="w-full flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-bold text-white transition-all active:scale-95 disabled:opacity-50"
                        style={{ background: '#7f1d1d' }}
                      >
                        {isProcessing ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
                        Force-End Room
                      </button>
                    )}

                    {/* Hide the offending account entirely (portfolio violation). */}
                    {reportTargetUserId(r) && (
                      <button
                        disabled={!!isProcessing}
                        onClick={() => handleHideAccount(r)}
                        className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-bold text-white transition-all active:scale-95 disabled:opacity-50"
                        style={{ background: '#7c2d12' }}
                      >
                        {isProcessing ? <Loader2 className="w-3 h-3 animate-spin" /> : <AlertTriangle className="w-3 h-3" />}
                        Hide Account
                      </button>
                    )}

                    <div className="flex gap-2">
                      <button
                        disabled={!!isProcessing}
                        onClick={() => handleAction(r.id, 'resolved')}
                        className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-bold text-white transition-all active:scale-95 disabled:opacity-50"
                        style={{ background: '#16a34a' }}
                      >
                        {isProcessing ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
                        Resolve
                      </button>
                      <button
                        disabled={!!isProcessing}
                        onClick={() => handleAction(r.id, 'dismissed')}
                        className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-bold text-white/70 transition-all active:scale-95 disabled:opacity-50"
                        style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)' }}
                      >
                        {isProcessing ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
                        Dismiss
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {inspectingChat && (
        <WatcherChatInspectorModal
          chatId={inspectingChat.chatId}
          label={inspectingChat.label}
          onClose={() => setInspectingChat(null)}
        />
      )}
    </div>
  );
}

// ─── Main Panel ───────────────────────────────────────────────────────────────

export function WatcherActionPanel({ onClose, onNavigateToReports }: WatcherActionPanelProps) {
  const { userProfile } = useUserData();
  const [view, setView] = useState<PanelView>('home');
  const [pendingCount, setPendingCount] = useState<number | null>(null);
  const unsubRef = useRef<(() => void) | null>(null);

  const watcherId  = String(userProfile?.id || '');
  const watcherUsername = String(userProfile?.username || 'watcher');

  // Live listener for pending report count
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const db = await getDbSafe();
        const q = query(collection(db, 'reports'), where('status', '==', 'pending'));
        const unsub = onSnapshot(q, snap => {
          if (active) setPendingCount(snap.size);
        }, () => {});
        unsubRef.current = unsub;
      } catch {
        // silently ignore if no permission yet
      }
    })();
    return () => {
      active = false;
      unsubRef.current?.();
    };
  }, []);

  const HOME_ACTIONS = [
    { icon: <VolumeX className="w-4 h-4" />, label: 'Mute / Unmute User', view: 'mute' as PanelView },
    { icon: <Trash2 className="w-4 h-4" />,  label: 'Soft-Delete Post',    view: 'delete' as PanelView },
    { icon: <ScrollText className="w-4 h-4" />, label: 'Audit Feed',        view: 'audit' as PanelView },
  ];

  return (
    <motion.div
      className="fixed inset-0 flex items-end justify-center pb-6 px-4"
      style={{ zIndex: 9999995 }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="w-full max-w-sm rounded-3xl overflow-hidden"
        style={{ background: '#0a0a0a', border: '1.5px solid #DC2626', boxShadow: '0 0 60px rgba(220,38,38,0.35)' }}
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 60, opacity: 0 }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-8 pb-4">
          <div className="flex items-center gap-2">
            <Eye className="w-5 h-5" style={{ color: '#DC2626' }} />
            <span className="font-black text-white text-sm tracking-wide">Watcher Remote</span>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white text-xl leading-none">✕</button>
        </div>
        <div className="mx-6 h-px" style={{ background: 'rgba(220,38,38,0.2)' }} />

        {/* Body */}
        <div className="px-6 py-6 pb-8">
          <AnimatePresence mode="wait">
            {view === 'home' && (
              <motion.div key="home" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-3">
                <p className="text-white/30 text-[11px] uppercase tracking-widest font-bold mb-3">Select Action</p>

                {/* Reports — first-come-first-serve live feed */}
                <button
                  onClick={() => setView('reports')}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-all text-white"
                  style={{
                    background: pendingCount ? 'rgba(220,38,38,0.08)' : 'rgba(255,255,255,0.04)',
                    borderColor: pendingCount ? 'rgba(220,38,38,0.4)' : 'rgba(255,255,255,0.06)',
                  }}
                >
                  <div className="flex items-center gap-3 text-sm font-semibold">
                    <span style={{ color: '#DC2626' }}><Flag className="w-4 h-4" /></span>
                    <span>
                      Reports
                      {pendingCount !== null && (
                        <span
                          className="ml-2 inline-flex items-center justify-center rounded-full text-[11px] font-black px-2 py-0.5 min-w-[22px]"
                          style={{
                            background: pendingCount > 0 ? '#DC2626' : 'rgba(255,255,255,0.1)',
                            color: '#fff',
                            boxShadow: pendingCount > 0 ? '0 0 8px rgba(220,38,38,0.6)' : 'none',
                          }}
                        >
                          {pendingCount}
                        </span>
                      )}
                    </span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-white/30" />
                </button>

                {HOME_ACTIONS.map(a => (
                  <button
                    key={a.view}
                    onClick={() => setView(a.view)}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-white/4 hover:bg-white/8 border border-white/6 hover:border-[#DC2626]/40 transition-all text-white"
                  >
                    <div className="flex items-center gap-3 text-sm font-semibold">
                      <span style={{ color: '#DC2626' }}>{a.icon}</span>
                      {a.label}
                    </div>
                    <ChevronRight className="w-4 h-4 text-white/30" />
                  </button>
                ))}
              </motion.div>
            )}
            {view === 'mute' && (
              <motion.div key="mute" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <MuteView watcherId={watcherId} watcherUsername={watcherUsername} onBack={() => setView('home')} />
              </motion.div>
            )}
            {view === 'delete' && (
              <motion.div key="delete" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <DeleteView watcherId={watcherId} watcherUsername={watcherUsername} onBack={() => setView('home')} />
              </motion.div>
            )}
            {view === 'audit' && (
              <motion.div key="audit" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <AuditFeed onBack={() => setView('home')} />
              </motion.div>
            )}
            {view === 'reports' && (
              <motion.div key="reports" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <ReportsView watcherId={watcherId} watcherUsername={watcherUsername} onBack={() => setView('home')} onClose={onClose} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </motion.div>
  );
}
